import { Link } from "react-router-dom";
import { Building2, UserCheck, LifeBuoy, ShieldAlert, FileText, Info as InfoIcon } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/i18n/I18nContext";
import { useLoad, Loadable, Status, fmt, can, label } from "@/components/control/kit";
import { taskTarget } from "@/components/control/TaskPanel";

// C02: greeting, four work counters, the three most urgent own tasks, service status and recent admin actions.
const KPIS = [
  ["orgs_to_review", "Organisationen prüfen", Building2, "/organisationen/ansprueche"],
  ["pending_reviews", "Profilprüfungen", UserCheck, "/profilpruefungen"],
  ["open_support", "Supportfälle", LifeBuoy, "/support"],
  ["open_reports", "Meldungen offen", ShieldAlert, "/meldungen-sicherheit"],
];

const greeting = () => {
  const h = new Date().getHours();
  return h < 11 ? "Guten Morgen" : h < 18 ? "Guten Tag" : "Guten Abend";
};

function Kpis({ k }) {
  const items = KPIS.filter(([key]) => k[key] != null);
  return (
    <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="overview-kpis">
      {items.map(([key, text, Icon, to]) => (
        <Link key={key} to={to} data-testid={`kpi-${key}`} className="rounded-2xl border border-[#E6E1F0] bg-white p-4 transition-colors hover:border-brand-magenta">
          <p className="flex items-center gap-2 text-2xl font-bold text-brand-purple"><Icon className="h-5 w-5 text-brand-magenta" aria-hidden="true" />{k[key]}</p>
          <p className="mt-3 text-sm font-semibold text-zinc-600">{text}</p>
        </Link>
      ))}
    </div>
  );
}

function TaskCards() {
  const st = useLoad("/tasks?scope=open");
  return (
    <section aria-labelledby="overview-tasks-title">
      <div className="mb-3 flex items-end justify-between gap-2">
        <h2 id="overview-tasks-title" className="text-xl font-bold text-brand-purple">Meine aktuellen Aufgaben</h2>
        <Link to="/aufgaben" className="text-sm font-semibold text-brand-purple underline" data-testid="overview-all-tasks">Alle Aufgaben</Link>
      </div>
      <Loadable state={st}>
        {(d) => (d.tasks.length === 0 ? <p className="rounded-xl border border-[#E6E1F0] bg-white p-4 text-sm text-zinc-600">Keine offenen Aufgaben.</p> : (
          <ul className="space-y-3" data-testid="overview-task-cards">
            {d.tasks.slice(0, 3).map((t) => (
              <li key={`${t.kind}-${t.id}`} className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-[#E6E1F0] bg-white p-4">
                <div className="flex min-w-0 items-start gap-3">
                  <FileText className="mt-1 h-5 w-5 shrink-0 text-brand-magenta" aria-hidden="true" />
                  <div className="min-w-0">
                    <p className="font-bold text-brand-purple">{t.title || t.label}</p>
                    <p className="text-sm text-zinc-600">{t.label} · {t.assigned_to?.name || "Nicht zugewiesen"} · {label(t.priority)}</p>
                    <div className="mt-2"><Status value={t.status} /></div>
                  </div>
                </div>
                <Link to={taskTarget(t)} data-testid={`overview-open-task-${t.id}`} className="self-center text-sm font-bold text-brand-magenta hover:underline">Aufgabe öffnen</Link>
              </li>
            ))}
          </ul>
        ))}
      </Loadable>
    </section>
  );
}

function ServiceCard() {
  const { user } = useAuth();
  const st = useLoad(can(user, "system:view") ? "/system/status" : null);
  const broken = st.data?.services?.some((s) => s.state === "gestört");
  const title = !st.data ? "Dienststatus unbekannt" : broken ? "Störung festgestellt" : "Dienststatus";
  const text = !st.data ? "Prüfung ausstehend. Noch keine aktuellen Prüfdaten."
    : broken ? "Mindestens ein Dienst ist gestört. Details im Systemstatus."
      : `Momentaufnahme ${fmt(st.data.checked_at)} ohne festgestellte Störung. Keine dauerhafte Überwachung.`;
  return (
    <div className="rounded-2xl bg-[#EEEAF7] p-4" data-testid="overview-service-status">
      <p className="flex items-center gap-2 font-bold text-brand-purple"><InfoIcon className="h-5 w-5" aria-hidden="true" />{title}</p>
      <p className="mt-1 text-sm text-zinc-600">{text}</p>
    </div>
  );
}

function RecentActions({ items }) {
  const { t } = useI18n();
  const names = t("audit.actions");
  if (!items) return null;
  return (
    <section aria-labelledby="overview-actions-title">
      <h2 id="overview-actions-title" className="mb-3 text-xl font-bold text-brand-purple">Letzte Verwaltungsaktionen</h2>
      <ul className="space-y-2.5" data-testid="overview-recent-actions">
        {items.slice(0, 3).map((a) => (
          <li key={a.id} className="rounded-xl border border-[#E6E1F0] bg-white px-4 py-3">
            <p className="text-sm font-semibold text-zinc-600">{(names && names[a.action]) || a.action}</p>
            <p className="text-base text-brand-purple">{a.actor_name || a.actor_email} · {fmt(a.timestamp)}</p>
          </li>
        ))}
        {items.length === 0 && <li className="text-sm text-zinc-600">Noch keine Ereignisse.</li>}
      </ul>
    </section>
  );
}

export default function Overview() {
  const { user } = useAuth();
  const st = useLoad("/overview/stats");
  return (
    <div data-testid="overview-page" className="animate-fade-in-up">
      <p className="hidden text-xs font-semibold text-zinc-500 lg:block">Übersicht / Plattformverwaltung</p>
      <p className="text-xs font-semibold text-zinc-500 lg:hidden">{user?.name} · {user?.role_label}</p>
      <h1 className="mt-3 text-3xl font-bold tracking-tight text-brand-purple lg:text-[2.15rem]" data-testid="overview-greeting">{greeting()}, {(user?.name || "").replace(/\.+$/, "")}.</h1>
      <p className="mt-2 text-base text-zinc-600">Deine wichtigsten Prüfungen und Aufgaben auf einen Blick.</p>
      <div className="mt-6"><Loadable state={st}>{(d) => <Kpis k={d.kpis} />}</Loadable></div>
      <div className="mt-6 grid gap-6 lg:grid-cols-[minmax(0,1fr)_18.5rem]">
        <TaskCards />
        <div className="space-y-6"><ServiceCard /><RecentActions items={st.data?.recent_audit} /></div>
      </div>
    </div>
  );
}
